import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, RefreshCw, ClipboardList, CalendarDays } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import TaskCard from './TaskCard';
import CompleteTaskDialog from './CompleteTaskDialog';
import NotDoneDialog from './NotDoneDialog';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { API, isMyTask } from '../../utils/kitchenTasks';
import { formatDateFR } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const ORDER = { refusee: 0, en_cours: 1, a_faire: 2, a_valider: 3, terminee: 4, non_realisee: 5, validee: 6, annulee: 7 };
const DONE = ['terminee', 'non_realisee', 'validee', 'annulee'];
const sortTasks = (list) => [...list].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));

// Shared daily view: every task of the day; mine → swipe, unassigned → take, others → read-only
const MyKitchenTasks = ({ date, onDateChange, onCountsChange }) => {
  const { user, hasPermission } = useAuth();
  const canActAll = hasPermission('edit_task');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [notDone, setNotDone] = useState(null);

  const fetchTasks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/kitchen-tasks`, { params: { date } });
      const data = sortTasks(res.data.data || []);
      setTasks(data);
      onCountsChange?.(data);
    } catch (err) { if (!silent) toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, [date, onCountsChange]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useLiveRefresh(() => fetchTasks(true));

  const act = async (task, fn, msg) => {
    if (busyId) return false;
    setBusyId(task.id);
    try {
      const res = await fn();
      const updated = res.data.data;
      setTasks(prev => sortTasks(prev.map(t => (t.id === updated.id ? updated : t))));
      toast.success(msg);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 409) fetchTasks(true);
      return false;
    } finally { setBusyId(null); }
  };

  const handleTake = (task) => act(task, () => axios.post(`${API}/kitchen-tasks/${task.id}/take`), 'Tâche prise en charge');
  const handleComplete = async (payload) => {
    const ok = await act(completing, () => axios.post(`${API}/kitchen-tasks/${completing.id}/complete`, payload), 'Tâche terminée !');
    if (ok) setCompleting(null);
  };
  const handleNotDone = async (payload) => {
    const ok = await act(notDone, () => axios.post(`${API}/kitchen-tasks/${notDone.id}/not-done`, payload), 'Tâche marquée non réalisée');
    if (ok) setNotDone(null);
  };

  const modeFor = useCallback((t) => {
    if (DONE.includes(t.status)) return 'readonly';
    if (!(t.assigned_user_ids || []).length) return canActAll ? 'mine' : 'unassigned';
    if (isMyTask(t, user) || canActAll) return 'mine';
    return 'other';
  }, [user, canActAll]);

  const { mine, available, others, done } = useMemo(() => {
    const g = { mine: [], available: [], others: [], done: [] };
    tasks.forEach(t => {
      const m = modeFor(t);
      if (m === 'readonly') g.done.push(t);
      else if (m === 'unassigned') g.available.push(t);
      else if (m === 'other') g.others.push(t);
      else g.mine.push(t);
    });
    return g;
  }, [tasks, modeFor]);

  const locked = !!busyId || !!completing || !!notDone;
  const renderCard = (t) => (
    <TaskCard key={t.id} task={t} mode={modeFor(t)} busy={busyId === t.id} locked={locked}
      onSwipeRight={(task) => { if (!locked) setCompleting(task); }}
      onSwipeLeft={(task) => { if (!locked) setNotDone(task); }}
      onTake={handleTake} />
  );

  const Section = ({ title, items, testId, hint }) => items.length === 0 ? null : (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-heading text-base md:text-lg font-bold text-gray-800">{title} <span className="text-gray-400 font-normal">({items.length})</span></h3>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {items.map(renderCard)}
    </div>
  );

  return (
    <section className="max-w-2xl mx-auto mb-10" data-testid="my-kitchen-tasks">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2"><ClipboardList size={22} className="text-neon-pink" />Tâches du jour</h2>
          <p className="text-gray-500 text-sm" data-testid="my-tasks-summary">{formatDateFR(date)} · {mine.length} pour moi · {available.length} à prendre · {others.length} en cours par l'équipe</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="relative h-11 flex items-center gap-2 px-3 rounded-xl bg-white border border-gray-200 text-sm text-gray-700 cursor-pointer">
            <CalendarDays size={16} className="text-gray-500" />
            <input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} className="bg-transparent focus:outline-none" data-testid="my-tasks-date-input" />
          </label>
          <Button variant="outline" size="icon" onClick={() => fetchTasks()} className="h-11 w-11 rounded-xl bg-white border-gray-200 text-gray-700" data-testid="my-tasks-refresh-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 text-neon-pink animate-spin" /></div>
      ) : tasks.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 text-center" data-testid="my-tasks-empty">
          <p className="text-gray-700 font-medium">Aucune tâche pour cette journée.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="my-tasks-list">
          <Section title={canActAll ? 'Tâches à traiter' : 'Mes tâches'} items={mine} testId="my-tasks-mine" hint="Glissez pour valider" />
          <Section title="Tâches à prendre" items={available} testId="my-tasks-available" hint="Non affectées" />
          <Section title="Tâches de l'équipe" items={others} testId="my-tasks-others" hint="Lecture seule" />
          {done.length > 0 && (
            <details className="bg-gray-50 border border-gray-200 rounded-2xl p-3" data-testid="my-tasks-done">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 min-h-[32px] flex items-center">{done.length} tâche{done.length > 1 ? 's' : ''} clôturée{done.length > 1 ? 's' : ''} (terminées, non réalisées, annulées)</summary>
              <div className="space-y-3 mt-3">{done.map(renderCard)}</div>
            </details>
          )}
        </div>
      )}

      <CompleteTaskDialog task={completing} open={!!completing} onOpenChange={(v) => { if (!v) setCompleting(null); }} onConfirm={handleComplete} busy={!!busyId} />
      <NotDoneDialog task={notDone} open={!!notDone} onOpenChange={(v) => { if (!v) setNotDone(null); }} onConfirm={handleNotDone} busy={!!busyId} />
    </section>
  );
};

export default MyKitchenTasks;
